import { Static, Union } from "runtypes";

import {
  BooleanColumnDisplayFormat,
  DateColumnDisplayFormat,
  DatetimeColumnDisplayFormat,
  NumberColumnDisplayFormat,
} from "../display-table/index.js";
import { FilledDynamicValueTableColumnType } from "../DynamicValue.js";
import { HqlAggregationFunction } from "../hql/types.js";
import { ScopeItemType } from "../scopeTypes.js";

export const MetricDisplayFormat = Union(
  NumberColumnDisplayFormat,
  DatetimeColumnDisplayFormat,
  DateColumnDisplayFormat,
  BooleanColumnDisplayFormat,
);
export type MetricDisplayFormat = Static<typeof MetricDisplayFormat>;

export const DEFAULT_DATE_DISPLAY_FORMAT: DateColumnDisplayFormat = {
  columnType: "DATE",
  format: "PLAIN",
};

export const DEFAULT_DATETIME_DISPLAY_FORMAT: DatetimeColumnDisplayFormat &
  Required<Pick<DatetimeColumnDisplayFormat, "timezone">> = {
  columnType: "DATETIME",
  format: "PLAIN",
  timezone: "IGNORE",
};

export const DEFAULT_NUMBER_DISPLAY_FORMAT: NumberColumnDisplayFormat = {
  columnType: "NUMBER",
  format: "NUMBER",
  numDecimalDigits: -1,
  currency: "USD",
  abbreviateLargeNumbers: false,
  showSeparators: true,
};

export const DEFAULT_TABLE_NUMBER_DISPLAY_FORMAT: NumberColumnDisplayFormat = {
  ...DEFAULT_NUMBER_DISPLAY_FORMAT,
  // we don't want to show separators for IDs in tables
  showSeparators: false,
  nanFormat: "null",
};

export const DEFAULT_BOOLEAN_DISPLAY_FORMAT: BooleanColumnDisplayFormat = {
  columnType: "BOOLEAN",
  format: "PLAIN",
};

export const getDefaultDisplayFormat = (
  scopeType: ScopeItemType | FilledDynamicValueTableColumnType | unknown,
  aggregationType?: HqlAggregationFunction | null,
):
  | DateColumnDisplayFormat
  | DatetimeColumnDisplayFormat
  | NumberColumnDisplayFormat
  | BooleanColumnDisplayFormat
  | undefined => {
  // These specific aggregation types are unique in that they will always return
  // a number regardless of the type of the column.
  if (
    aggregationType === HqlAggregationFunction.Count ||
    aggregationType === HqlAggregationFunction.CountDistinct
  ) {
    return DEFAULT_NUMBER_DISPLAY_FORMAT;
  }
  switch (scopeType) {
    case ScopeItemType.DATE:
    case FilledDynamicValueTableColumnType.DATE:
      return DEFAULT_DATE_DISPLAY_FORMAT;
    case ScopeItemType.DATETIME:
    case FilledDynamicValueTableColumnType.DATETIME:
    case FilledDynamicValueTableColumnType.DATETIMETZ:
      return DEFAULT_DATETIME_DISPLAY_FORMAT;
    case FilledDynamicValueTableColumnType.BOOLEAN:
    case ScopeItemType.BOOLEAN:
      return DEFAULT_BOOLEAN_DISPLAY_FORMAT;
    case ScopeItemType.DATAFRAME:
    case ScopeItemType.REMOTE_DATAFRAME:
    case ScopeItemType.SNOWPARK_DATAFRAME:
      // we will set the display format based on the type of the column
      return undefined;
    default:
      return DEFAULT_NUMBER_DISPLAY_FORMAT;
  }
};

// For magic generation usage only
export const NUMERIC_COLUMN_TYPES = [
  "float64",
  "int64",
  "float32",
  "int32",
  "int8",
  "int16",
  "uint8",
  "uint16",
  "uint32",
  "uint64",
  "float16",
  "float128",
  "complex64",
  "complex128",
  "complex256",
];

// For now we only worry about supporting numbers and dates here, because this is used specifically for
// formatting, so just return UNKNOWN for anything else
export const getScopeItemTypeForDataframeColumnType = (
  columnType: string,
  aggregation?: HqlAggregationFunction | null,
): ScopeItemType => {
  // These specific aggregation types are unique in that they will always return
  // a number regardless of the type of the column.
  if (
    aggregation === HqlAggregationFunction.Count ||
    aggregation === HqlAggregationFunction.CountDistinct
  ) {
    return ScopeItemType.NUMBER;
  }
  if (NUMERIC_COLUMN_TYPES.includes(columnType)) {
    return ScopeItemType.NUMBER;
  } else if (columnType.startsWith("datetime")) {
    return ScopeItemType.DATETIME;
  } else if (columnType.startsWith("date")) {
    return ScopeItemType.DATE;
  } else {
    return ScopeItemType.UNKNOWN;
  }
};
